import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Flame, Package, Sparkles, Loader2 } from "lucide-react";

export default function Fusion() {
  const { refreshUser } = useOutletContext();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/inventory");
    setItems(data.items.filter((i) => ["Common", "Uncommon"].includes(i.rarity)));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id, qty) => setSel((s) => {
    const c = s.filter((x) => x === id).length;
    if (s.length < 3 && c < qty) return [...s, id];   // add one instance (up to owned qty / total 3)
    const i = s.indexOf(id);                            // otherwise remove one instance
    if (i >= 0) { const n = [...s]; n.splice(i, 1); return n; }
    return s;
  });

  const fuse = async () => {
    if (sel.length !== 3) { toast.error("Select exactly 3 low-tier items."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/fusion", { item_ids: sel });
      setResult(data);
      if (data.success) { toast.success(`Fusion success! Forged ${data.reward.name}!`); }
      else toast("The fusion collapsed — nothing survived.", { icon: "💥" });
      setSel([]); refreshUser?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fusion failed"); } finally { setBusy(false); }
  };

  const count = (id) => sel.filter((x) => x === id).length;
  return (
    <div data-testid="fusion-page" className="max-w-3xl mx-auto">
      <span className="label-cap text-primary flex items-center gap-2"><Flame className="w-4 h-4" /> Fusion Burner</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1">The Incinerator</h1>
      <p className="text-muted-foreground mb-5">Burn <b className="text-foreground">3 low-tier items</b> for a <b className="text-primary">10% chance</b> at a random mid-tier (Rare) upgrade. Fusion is permanent.</p>

      <div className="panel-glow p-4 mb-5">
        <div className="label-cap mb-2">Feedstock ({sel.length}/3 selected)</div>
        {items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No Common/Uncommon items to fuse.</p> : (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {items.map((it) => {
              const Icon = Icons[it.icon?.replace(/(^|-)([a-z])/g, (_, s, c) => c.toUpperCase())] || Package;
              const n = count(it.id);
              return (
                <button key={it.id} data-testid={`fusion-item-${it.id}`} onClick={() => toggle(it.id, it.qty)}
                  className={`panel p-2 flex flex-col items-center gap-1 relative transition-colors ${n ? "border-primary" : "hover:border-primary/50"}`}>
                  <Icon className="w-6 h-6 text-primary" />
                  <span className="text-[9px] font-bold text-center leading-none truncate w-full">{it.name}</span>
                  {it.qty > 1 && <span className="absolute top-0.5 left-0.5 text-[8px] mono bg-muted rounded px-1">x{it.qty}</span>}
                  {n > 0 && <span className="absolute top-0.5 right-0.5 text-[9px] mono bg-primary text-primary-foreground rounded-full w-4 h-4 grid place-items-center">{n}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-center">
        <button data-testid="fusion-burn-btn" onClick={fuse} disabled={busy || sel.length !== 3} className="btn-primary text-base px-10 py-4">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Fusing…</> : <><Flame className="w-5 h-5" /> Incinerate &amp; Fuse</>}
        </button>
      </div>

      {result && (
        <div data-testid="fusion-result" className={`panel-glow p-6 mt-6 text-center ${result.success ? "" : "opacity-90"}`}>
          {result.success ? (
            <><Sparkles className="w-10 h-10 text-primary mx-auto mb-2" /><div className="label-cap">Upgrade forged</div>
              <div className="font-head font-black text-2xl text-primary">{result.reward.name}</div>
              <div className="label-cap mt-1">{result.reward.rarity}</div></>
          ) : (
            <><Flame className="w-10 h-10 text-destructive mx-auto mb-2" /><div className="font-head font-bold text-lg">Fusion Collapsed</div>
              <p className="text-sm text-muted-foreground">The core destabilised. Better luck next burn.</p></>
          )}
        </div>
      )}
    </div>
  );
}
